import { useEffect, useState } from 'react';
import { billingService } from '../services/api';
import type { BillingInfo } from '../types/api';
import { formatDailyTime } from './format';

// Paying for a plan (docs/REQUIREMENTS.md PLAN-6, PLAN-17): Paddle's overlay checkout runs on the website
// (webots.academy/checkout, backend/app/public/checkout.py), the checkout domain Paddle approved; the app runs no
// Paddle.js. A buy button asks the API for a short-lived link to that page and goes there; after paying, Paddle sends the
// browser back here with ?checkout=success (components/CheckoutReturn.tsx). The plan itself is set by Paddle's webhook
// on the backend (app/utils/paddle.py). Without Paddle configured (local, tests), upgrading is an email.

export type CheckoutPlan = 'student' | 'class';

export const PLANS_EMAIL = 'contact@webots.academy';
export function upgradeMailto(subject: string, body = ''): string {
  return `mailto:${PLANS_EMAIL}?subject=${encodeURIComponent(subject)}${body ? `&body=${encodeURIComponent(body)}` : ''}`;
}

// Whether online payment is on (`checkout` of GET /api/billing/me is null without Paddle). It doesn't change while the app
// runs, so it is asked once per page load; components that load the billing info anyway hand it over
// (`notePaymentsConfigured`). While it is off, buy buttons offer a free month instead (PLAN-18, components/TrialRequest).
let paymentsKnown: boolean | null = null;
let paymentsQuery: Promise<boolean> | null = null;

export function notePaymentsConfigured(info: BillingInfo): void {
  paymentsKnown = info.checkout !== null;
}

function paymentsConfigured(): Promise<boolean> {
  if (paymentsKnown !== null) return Promise.resolve(paymentsKnown);
  paymentsQuery ??= billingService.me().then((info) => {
    notePaymentsConfigured(info);
    return info.checkout !== null;
  }).finally(() => { paymentsQuery = null; });
  return paymentsQuery;
}

// null until known (or when the API can't be reached: the buy button then stays, as before).
export function usePaymentsConfigured(): boolean | null {
  const [configured, setConfigured] = useState<boolean | null>(paymentsKnown);
  useEffect(() => {
    if (configured !== null) return;
    let alive = true;
    paymentsConfigured().then((value) => { if (alive) setConfigured(value); }).catch(() => undefined);
    return () => { alive = false; };
  }, [configured]);
  return configured;
}

// The query parameters the checkout page adds to the way back (CheckoutReturn reads and removes them).
export const CHECKOUT_RETURN_PARAMS = ['checkout', 'plan', 'class'];

// This page, to come back to after paying or closing the checkout, without an earlier checkout's parameters.
function currentPath(): string {
  const params = new URLSearchParams(window.location.search);
  CHECKOUT_RETURN_PARAMS.forEach((name) => params.delete(name));
  const query = params.toString();
  return `${window.location.pathname}${query ? `?${query}` : ''}`;
}

interface CheckoutOptions {
  // The class a Class plan pays for.
  classId?: number;
  className?: string;
}

// Goes to the checkout for a plan: true when the browser is leaving for the checkout page, false when an email opened
// instead (no Paddle). Throws when the API can't make the link.
export async function startCheckout(plan: CheckoutPlan, { classId, className }: CheckoutOptions = {}): Promise<boolean> {
  const info: BillingInfo = await billingService.me();
  if (!info.checkout) {
    window.location.href = plan === 'student'
      ? upgradeMailto('Student plan', `I would like the Student plan (€${info.prices.student.amount}/month, ${formatDailyTime(info.limits.student_seconds_per_day)} of simulation a day).`)
      : upgradeMailto(`Activate the class "${className ?? classId}"`, `Please activate the Class plan for my class "${className ?? ''}" (id ${classId}).`);
    return false;
  }
  // The same tab: the checkout page sends the browser back here when it is done.
  window.location.assign(await billingService.checkoutLink(plan, plan === 'class' ? classId : undefined, currentPath()));
  return true;
}

// Paddle's customer portal: change the card, see invoices, cancel the Student plan.
export async function openBillingPortal(): Promise<void> {
  window.location.href = await billingService.portal();
}
